export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationOptions {
  title: string;
  message: string;
  type?: NotificationType;
  duration?: number;
  icon?: string;
  requireInteraction?: boolean;
  data?: Record<string, any>;
  onClick?: () => void;
}

class NotificationManager {
  private static instance: NotificationManager;
  private useNativeNotifications: boolean = false;
  private permission: NotificationPermission = 'default';

  private constructor() {
    this.checkPermission();
  }

  public static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  private checkPermission(): void {
    if ('Notification' in window) {
      this.permission = Notification.permission;
      this.useNativeNotifications = this.permission === 'granted';
    }
  }

  public async requestPermission(): Promise<NotificationPermission> {
    console.log('[PUSH-PERMISSION] NotificationManager.requestPermission called');

    if (!('Notification' in window)) {
      console.warn('[PUSH-PERMISSION] Ce navigateur ne supporte pas les notifications');
      return 'denied';
    }

    if (this.permission === 'granted') {
      console.log('[PUSH-PERMISSION] Permission already granted, returning early');
      return 'granted';
    }

    try {
      console.log('[PUSH-PERMISSION] Calling Notification.requestPermission()');
      const permission = await Notification.requestPermission();
      console.log('[PUSH-PERMISSION] Browser returned permission:', permission);

      this.permission = permission;
      this.useNativeNotifications = permission === 'granted';

      // Sauvegarder la préférence
      localStorage.setItem('notificationPermission', permission);

      return permission;
    } catch (error) {
      console.error('[PUSH-PERMISSION] Erreur lors de la demande de permission:', error);
      return 'denied';
    }
  }

  public async show(options: NotificationOptions): Promise<void> {
    this.checkPermission();

    // Si les notifications natives sont disponibles et autorisées
    if (this.useNativeNotifications && 'Notification' in window) {
      this.showNativeNotification(options);
    } else {
      // Fallback vers les notifications Refine
      this.showRefineNotification(options);
    }
  }

  private showNativeNotification(options: NotificationOptions): void {
    const {
      title,
      message,
      type = 'info',
      icon = '/logo.png',
      requireInteraction = false,
      data = {},
      onClick
    } = options;

    const notificationOptions: NotificationOptions & { body?: string; badge?: string; tag?: string; vibrate?: number[] } = {
      title,
      message,
      body: message,
      icon: this.getIconForType(type, icon),
      badge: '/badge.png',
      tag: `safee-${type}-${Date.now()}`,
      requireInteraction,
      data: {
        ...data,
        type,
        timestamp: Date.now()
      },
      vibrate: [200, 100, 200]
    };
    // Préférer l'affichage via le Service Worker pour éviter le bandeau Chrome
    if ('serviceWorker' in navigator) {
      // Ne pas await: laisser le SW gérer la promesse
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, notificationOptions as any);
      }).catch(() => {
        // Fallback vers Notification API si le SW n'est pas prêt
        const fallback = new Notification(title, notificationOptions as any);
        if (onClick) {
          fallback.onclick = (event) => {
            event.preventDefault();
            window.focus();
            onClick();
            fallback.close();
          };
        } else {
          fallback.onclick = () => {
            window.focus();
            fallback.close();
          };
        }
        if (options.duration && options.duration > 0) {
          setTimeout(() => {
            fallback.close();
          }, options.duration);
        }
      });
      return;
    }

    // Fallback lorsque le SW n'est pas disponible
    const nativeNotification = new Notification(title, notificationOptions as any);
    if (onClick) {
      nativeNotification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        onClick();
        nativeNotification.close();
      };
    } else {
      nativeNotification.onclick = () => {
        window.focus();
        nativeNotification.close();
      };
    }
    if (options.duration && options.duration > 0) {
      setTimeout(() => {
        nativeNotification.close();
      }, options.duration);
    }
  }

  private showRefineNotification(options: NotificationOptions): void {
    const { title, message, type = 'info' } = options;

    // Fallback to console log if Refine notification provider is not available
    // In practice, the notification provider from @refinedev/mui is used in App.tsx
    console.log(`[${type.toUpperCase()}] ${title}: ${message}`);

    // Note: The actual Refine notification will be triggered by the notificationProvider
    // configured in App.tsx with @refinedev/mui
  }

  private getIconForType(type: NotificationType, defaultIcon: string): string {
    const icons: Record<NotificationType, string> = {
      success: '/icons/success.png',
      error: '/icons/error.png',
      warning: '/icons/warning.png',
      info: '/icons/info.png'
    };
    return icons[type] || defaultIcon;
  }

  public isNativeSupported(): boolean {
    return 'Notification' in window;
  }

  public isNativeEnabled(): boolean {
    return this.useNativeNotifications;
  }

  public getPermission(): NotificationPermission {
    return this.permission;
  }
}

export default NotificationManager.getInstance();
